// Selection is accepted only when the variant and selling-plan pair exists in the server-rendered catalog.
function resolveSelection(search, choices) {
  const p = new URLSearchParams(search);
  for (const key of ['variant', 'selling_plan', 'quantity']) if (p.getAll(key).length > 1) return null;
  const variant = p.get('variant');
  const plan = p.get('selling_plan') || '';
  const rawQuantity = p.get('quantity') ?? '1';
  if (!/^\d+$/.test(variant || '') || (plan && !/^\d+$/.test(plan))) return null;
  if (!/^[1-9]\d*$/.test(rawQuantity)) return null;
  const quantity = Number(rawQuantity);
  if (!Number.isSafeInteger(quantity) || quantity > 999) return null;
  const choice = choices.find((c) => c.variant === variant && c.plan === plan);
  if (!choice) return null;
  return {...choice, quantity, totalCents: Number(choice.price) * quantity, totalTubes: Number(choice.tubes) * quantity};
}

function selectionRecord(selected) {
  if (!selected) return null;
  return {
    product: selected.product, variant_id: selected.variant, quantity: String(selected.quantity),
    selling_plan_id: selected.plan, plan: selected.planLabel,
    price: (selected.totalCents / 100).toFixed(2), unit_price: (Number(selected.price) / 100).toFixed(2),
    currency: selected.currency, offer: selected.title, tubes: String(selected.totalTubes),
    free_tubes: String(Number(selected.gifts) * selected.quantity),
    guarantee_days: selected.guarantee, refill_days: selected.refillDays,
    renewal_price: selected.plan ? (Number(selected.renewal) / 100).toFixed(2) : '',
  };
}

// Change only the purchase mode of the current valid bundle; preserve quantity and attribution.
function changePurchaseMode(search, choices, subscribe, targetPlan = '') {
  const current = resolveSelection(search, choices);
  if (!current) return null;
  const target = choices.find((choice) => choice.variant === current.variant && (subscribe ? choice.plan === targetPlan && !!choice.plan : choice.plan === ''));
  if (!target) return null;
  const p = new URLSearchParams(search);
  if (subscribe) p.set('selling_plan', target.plan);
  else p.delete('selling_plan');
  return '?' + p.toString();
}

const PENDING = 'fv-waitlist-pending';
const SELECTION = 'fv-reserve-selection';
const safeRead = (key) => {try {return sessionStorage.getItem(key);} catch {return null;}};
const safeWrite = (key, value) => {try {sessionStorage.setItem(key, value);} catch { /* Native form still works. */ }};

class FvReserve extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.cards = [...this.querySelectorAll('[data-reserve-choice]')];
    this.choices = this.cards.map((card) => ({...card.dataset}));
    this.success = this.querySelector('[data-waitlist-success]');
    let search = location.search;
    if (this.success && !new URLSearchParams(search).has('variant')) search = safeRead(SELECTION) || search;
    this.applySelection(search);

    this.addEventListener('change', (event) => {
      const control = event.target.closest?.('[data-reserve-subscribe]');
      if (!control || this.success) return;
      const next = changePurchaseMode(this.selectionSearch, this.choices, control.checked, control.dataset.upgradePlan);
      if (!next) {control.checked = !!this.selected?.plan; return;}
      this.applySelection(next, true);
      this.cards.find((card) => !card.hidden)?.querySelector('[data-reserve-subscribe]')?.focus({preventScroll: true});
    });
    const form = this.querySelector('#fv-waitlist-form');
    form?.addEventListener('submit', (event) => {
      // Read the current state at submission time, including changes made with the subscription checkbox.
      const selected = this.selected;
      if (!selected || !form.checkValidity()) {event.preventDefault(); form.reportValidity(); return;}
      const p = new URLSearchParams(this.selectionSearch);
      const clean = (value) => String(value ?? '').trim().slice(0, 1000);
      const ua = navigator.userAgent;
      const details = {
        first_name: '', email: clean(new FormData(form).get('contact[email]')),
        ...selectionRecord(selected),
        country: clean(this.dataset.country), country_name: clean(this.dataset.countryName), language: clean(navigator.language),
        timezone: clean(Intl.DateTimeFormat().resolvedOptions().timeZone),
        device: /iPad|Tablet|Android(?!.*Mobile)/i.test(ua) ? 'tablet' : /Mobi|iPhone|Android/i.test(ua) ? 'mobile' : 'desktop',
        os: /iPhone|iPad|iPod/.test(ua) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Mac OS/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'Other',
        browser: /Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome\//.test(ua) ? 'Chrome' : /Safari\//.test(ua) ? 'Safari' : 'Other',
        screen: `${screen.width}x${screen.height}`, viewport: `${innerWidth}x${innerHeight}`,
        page_url: location.href, client_time: new Date().toISOString(),
      };
      for (const key of ['src', 'btn', 'ref', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) details[key] = clean(p.get(key));
      safeWrite(SELECTION, this.selectionSearch);
      safeWrite(PENDING, JSON.stringify(details));
    });
    if (this.success) {
      this.success.focus();
      const pending = safeRead(PENDING);
      if (pending && this.dataset.sheetUrl) {
        fetch(this.dataset.sheetUrl, {method: 'POST', mode: 'no-cors', headers: {'Content-Type': 'text/plain;charset=utf-8'}, body: pending, keepalive: true})
          .then(() => {try {sessionStorage.removeItem(PENDING);} catch { /* Optional storage. */ }})
          .catch(() => { /* Preserve pending data for retry. Opaque responses do not prove a spreadsheet write. */ });
      }
    } else this.querySelector('[data-waitlist-errors]')?.focus();
  }

  applySelection(search, updateURL = false) {
    const selected = resolveSelection(search, this.choices);
    this.selected = selected;
    this.selectionSearch = search;
    const missing = this.querySelector('[data-reserve-missing]');
    if (missing) missing.hidden = !!selected;
    const remedy = this.querySelector('[data-reserve-remedy]');
    if (remedy) remedy.hidden = !selected || !!this.success;
    const timer = this.querySelector('[data-reserve-countdown]');
    if (timer) timer.hidden = !selected || !!this.success;
    for (const card of this.cards) {
      card.hidden = !selected || card.dataset.variant !== selected.variant || card.dataset.plan !== selected.plan;
      const control = card.querySelector('[data-reserve-subscribe]');
      if (control) {
        control.checked = !!card.dataset.plan;
        control.disabled = !!this.success;
      }
    }
    const submit = this.querySelector('[data-reserve-submit]');
    if (submit) submit.disabled = !selected;
    if (!selected) return;
    const active = this.cards.find((card) => !card.hidden);
    const quantity = active?.querySelector('[data-reserve-quantity]');
    if (quantity) {
      quantity.hidden = selected.quantity <= 1;
      if (selected.quantity > 1) {
        const money = new Intl.NumberFormat('en-US', {style: 'currency', currency: selected.currency});
        quantity.textContent = `${selected.quantity} bundles · ${selected.totalTubes} tubes total · Selected total ${money.format(selected.totalCents / 100)}. Offer card shows the price per bundle.`;
      }
    }
    const tags = ['waitlist', 'restock', `variant-${selected.variant}`, `qty-${selected.quantity}`, selected.plan ? `selling-plan-${selected.plan}` : 'Plan: One-time', `Wants: ${selected.title}`, `Refill days: ${selected.refillDays}`];
    const tagsInput = this.querySelector('[data-waitlist-tags]');
    if (tagsInput) tagsInput.value = tags.map((v) => v.replaceAll(',', '').slice(0, 255)).join(',');
    safeWrite(SELECTION, search);
    if (updateURL) {
      const url = new URL(location.href);
      url.search = search;
      history.replaceState(history.state, '', url);
    }
  }
}
if (!customElements.get('fv-waitlist')) customElements.define('fv-waitlist', FvReserve);
